import { KeyboardEvent } from "react";
import { getKeyboardFocusableElements } from "../../../functions";

/**
 * Gets all visible focusable elements for a specific toolbar instance.
 * Filters out hidden items and includes the overflow trigger if present.
 */
function getToolbarElements(toolbarElement: HTMLElement) {
  const toolbarContent = toolbarElement.querySelector<HTMLElement>(
    '[data-anv="toolbar-content"]',
  );

  const mainItems = getKeyboardFocusableElements(toolbarContent).filter(
    (item) => {
      const isVisible = !item.closest('[aria-hidden="true"]');
      return isVisible;
    },
  );

  const overflowTrigger = toolbarElement.querySelector<HTMLElement>(
    '[data-anv="toolbar-overflow-trigger"]',
  );

  return overflowTrigger ? [...mainItems, overflowTrigger] : mainItems;
}

/**
 * Updates tabindex values for all items in a toolbar.
 * Sets tabindex="0" for the active item and tabindex="-1" for all others.
 */
export function updateToolbarItemsTabIndex(
  toolbarElement: HTMLElement,
  activeItem?: Element | number,
) {
  if (!toolbarElement) return;

  // Get all toolbar items
  const items = getToolbarElements(toolbarElement);
  if (!items.length) return;

  // Determine the active index - default to the first item
  let activeIndex = 0;

  if (typeof activeItem === "number") {
    // If activeItem is a number, use it directly
    activeIndex = Math.max(0, Math.min(activeItem, items.length - 1));
  } else if (activeItem instanceof Element) {
    // If activeItem is an element, find its index
    const foundIndex = items.indexOf(activeItem);
    if (foundIndex !== -1) {
      activeIndex = foundIndex;
    }
  }

  // Update tabindex values
  items.forEach((item, index) => {
    item.setAttribute("tabindex", index === activeIndex ? "0" : "-1");
  });

  return items[activeIndex];
}

/**
 * Handles keyboard navigation within the toolbar component following ARIA practices.
 *
 * Navigation behavior:
 * - Left/Right arrows move focus in horizontal toolbars
 * - Up/Down arrows move focus in vertical toolbars
 * - Home/End move to first/last items
 * - Navigation wraps around at the ends
 * - Only visible items are included in navigation
 */
export function handleToolbarKeyDown(
  event: KeyboardEvent<HTMLElement>,
  toolbarElement: HTMLElement,
) {
  if (!toolbarElement || event.altKey || event.ctrlKey || event.metaKey) return;

  const items = getToolbarElements(toolbarElement);
  if (!items.length) return;

  const activeElement = event.target as HTMLElement;
  if (!toolbarElement.contains(activeElement)) return;

  const activeItem = items.find(
    (item) =>
      item === activeElement ||
      Array.from(item.children).some((child) => child === activeElement),
  );

  const activeIndex = activeItem ? items.indexOf(activeItem) : -1;
  if (activeIndex === -1) return;

  let nextIndex = activeIndex;
  const isVertical =
    toolbarElement.getAttribute("aria-orientation") === "vertical";

  switch (event.key) {
    case "ArrowLeft":
    case "ArrowUp":
      event.preventDefault();
      if (
        (isVertical && event.key === "ArrowUp") ||
        (!isVertical && event.key === "ArrowLeft")
      ) {
        nextIndex = activeIndex <= 0 ? items.length - 1 : activeIndex - 1;
      }
      break;

    case "ArrowRight":
    case "ArrowDown":
      event.preventDefault();
      if (
        (isVertical && event.key === "ArrowDown") ||
        (!isVertical && event.key === "ArrowRight")
      ) {
        nextIndex = activeIndex >= items.length - 1 ? 0 : activeIndex + 1;
      }
      break;

    case "Home":
      event.preventDefault();
      nextIndex = 0;
      break;

    case "End":
      event.preventDefault();
      nextIndex = items.length - 1;
      break;

    default:
      return;
  }

  updateToolbarItemsTabIndex(toolbarElement, nextIndex);
  (items[nextIndex] as HTMLElement)?.focus();
}

/**
 * Handles keyboard navigation within overflow menu using arrow keys
 */
export const handleMenuKeyboardNavigation = (
  event: KeyboardEvent<HTMLUListElement>,
) => {
  const menuItems = Array.from(
    event.currentTarget.querySelectorAll('[role="menuitem"]'),
  ).map((menuItem) => {
    // Get first focusable element or fallback to menuitem itself
    return getKeyboardFocusableElements(menuItem as HTMLElement)[0] || menuItem;
  });

  if (!menuItems.length) return;

  const currentIndex = menuItems.indexOf(document.activeElement as HTMLElement);
  if (currentIndex === -1) return;

  let nextIndex = currentIndex;
  const isModifierKey = event.altKey || event.ctrlKey || event.metaKey;

  if (isModifierKey) return;

  switch (event.key) {
    case "ArrowDown":
    case "ArrowUp": {
      event.preventDefault();
      const direction = event.key === "ArrowDown" ? 1 : -1;
      nextIndex =
        (currentIndex + direction + menuItems.length) % menuItems.length;
      break;
    }
    case "Home":
      event.preventDefault();
      nextIndex = 0;
      break;
    case "End":
      event.preventDefault();
      nextIndex = menuItems.length - 1;
      break;
    default:
      return;
  }

  (menuItems[nextIndex] as HTMLElement)?.focus();
};
